import "@/i18n";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/AppLayout";

import Landing from "@/pages/Landing";
import Signup from "@/pages/Signup";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Users from "@/pages/Users";
import Customers from "@/pages/Customers";
import Products from "@/pages/Products";
import Invoices from "@/pages/Invoices";
import InvoiceForm from "@/pages/InvoiceForm";
import InvoiceDetail from "@/pages/InvoiceDetail";
import PublicInvoice from "@/pages/PublicInvoice";
import Settings from "@/pages/Settings";
import EmailOutbox from "@/pages/EmailOutbox";
import Exports from "@/pages/Exports";
import Payments from "@/pages/Payments";
import LegalDoc from "@/pages/LegalDoc";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminTenants from "@/pages/admin/AdminTenants";
import AdminTenantDetail from "@/pages/admin/AdminTenantDetail";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminPlatformSettings from "@/pages/admin/AdminPlatformSettings";
import AdminLanding from "@/pages/admin/AdminLanding";

import Quotes from "@/pages/Quotes";
import QuoteForm from "@/pages/QuoteForm";
import QuoteDetail from "@/pages/QuoteDetail";
import PublicQuote from "@/pages/PublicQuote";

import "@/App.css";

/** Guards `/app/*` — regular tenant users only. Super admins get redirected. */
function TenantRoute({ children }) {
  const { user, bootstrapping } = useAuth();
  if (bootstrapping) {
    return (
      <div
        className="flex h-screen items-center justify-center bg-slate-50 text-slate-500"
        data-testid="auth-bootstrapping"
      >
        <div className="text-sm">Loading...</div>
      </div>
    );
  }
  if (user?.is_super_admin) {
    return <Navigate to="/admin" replace />;
  }
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

/** Guards `/admin/*` — super admins only. Regular tenant users go to /app. */
function AdminRoute({ children }) {
  const { user, bootstrapping } = useAuth();
  if (bootstrapping) {
    return (
      <div
        className="flex h-screen items-center justify-center bg-[#0b1220] text-slate-400"
        data-testid="auth-bootstrapping"
      >
        <div className="text-sm">Loading platform admin…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!user.is_super_admin) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public marketing */}
          <Route path="/" element={<Landing />} />
          <Route path="/features" element={<Landing anchor="features" />} />
          <Route path="/pricing" element={<Landing anchor="pricing" />} />
          <Route path="/contact" element={<Landing anchor="contact" />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/login" element={<Login />} />
          <Route path="/legal/:kind" element={<LegalDoc />} />
          <Route path="/p/invoice/:token" element={<PublicInvoice />} />
          <Route path="/p/quote/:token" element={<PublicQuote />} />

          {/* Platform admin (super-admin only) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="tenants" element={<AdminTenants />} />
            <Route path="tenants/:id" element={<AdminTenantDetail />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="platform-settings" element={<AdminPlatformSettings />} />
            <Route path="landing" element={<AdminLanding />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          {/* Authenticated tenant app */}
          <Route
            path="/app"
            element={
              <TenantRoute>
                <AppLayout />
              </TenantRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="invoices/new" element={<InvoiceForm />} />
            <Route path="invoices/:id" element={<InvoiceDetail />} />
            <Route path="invoices/:id/edit" element={<InvoiceForm />} />
            <Route path="quotes" element={<Quotes />} />
            <Route path="quotes/new" element={<QuoteForm />} />
            <Route path="quotes/:id" element={<QuoteDetail />} />
            <Route path="quotes/:id/edit" element={<QuoteForm />} />
            <Route path="customers" element={<Customers />} />
            <Route path="products" element={<Products />} />
            <Route
              path="email-outbox"
              element={
                <ProtectedRoute roles={["admin", "accountant"]}>
                  <EmailOutbox />
                </ProtectedRoute>
              }
            />
            <Route
              path="payments"
              element={
                <ProtectedRoute roles={["admin", "accountant"]}>
                  <Payments />
                </ProtectedRoute>
              }
            />
            <Route
              path="exports"
              element={
                <ProtectedRoute roles={["admin", "accountant"]}>
                  <Exports />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="users"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <Users />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster richColors position="top-center" closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
